import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Receipt, Plus, Search, Filter, Download, Eye, Trash2, Send,
  DollarSign, Clock, CheckCircle2, XCircle, AlertCircle, Loader2,
  FileText, Building2, Calendar, X, ChevronRight, Printer,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = ['EUR', 'USD', 'GBP', 'AED', 'TRY', 'CHF'];
const TAX_RATES = [{ label: 'No Tax (0%)', value: 0 }, { label: '5%', value: 0.05 }, { label: '8%', value: 0.08 }, { label: '10%', value: 0.10 }, { label: '18%', value: 0.18 }, { label: '20%', value: 0.20 }];

const STATUS_CONFIG = {
  draft:     { label: 'Draft',     color: 'bg-slate-100 text-slate-600',   icon: FileText   },
  sent:      { label: 'Sent',      color: 'bg-blue-100 text-blue-700',     icon: Send       },
  paid:      { label: 'Paid',      color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  overdue:   { label: 'Overdue',   color: 'bg-red-100 text-red-700',       icon: AlertCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500',     icon: XCircle    },
};

const fmt = (v, currency = 'EUR') => {
  if (!v && v !== 0) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 }).format(v);
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.color}`}>
      <Icon className="w-3 h-3" />{cfg.label}
    </span>
  );
};

const EMPTY_ITEM = { description: '', quantity: 1, unit_price: 0 };

const downloadPDF = async (inv) => {
  const doc = new jsPDF();
  const primary = [22, 163, 74];  // green-600
  const gray    = [71, 85, 105];  // slate-600
  const lightGray = [248, 250, 252];

  // ── Header gradient bar ─────────────────────────────────────────────────
  doc.setFillColor(...primary);
  doc.rect(0, 0, 210, 36, 'F');
  // Subtle accent stripe
  doc.setFillColor(16, 185, 129);  // emerald-500
  doc.rect(0, 32, 210, 4, 'F');

  // Logo or brand name
  let logoLoaded = false;
  if (inv.logo_url) {
    try {
      const resp = await fetch(inv.logo_url);
      const blob = await resp.blob();
      const b64 = await new Promise(res => {
        const reader = new FileReader();
        reader.onloadend = () => res(reader.result);
        reader.readAsDataURL(blob);
      });
      const ext = blob.type.includes('png') ? 'PNG' : 'JPEG';
      doc.addImage(b64, ext, 10, 4, 28, 28);
      logoLoaded = true;
    } catch { /* fallback to text */ }
  }
  const brandX = logoLoaded ? 44 : 14;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(logoLoaded ? 16 : 22);
  doc.setFont('helvetica', 'bold');
  doc.text(inv.contractor_name || 'EXPO STAND', brandX, 16);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Contractor Portal · EXPO STAND', brandX, 24);

  // INVOICE label + number (right-aligned)
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('INVOICE', 196, 16, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(inv.invoice_number, 196, 24, { align: 'right' });

  // ── Status watermark ────────────────────────────────────────────────────
  if (inv.status === 'paid') {
    doc.setTextColor(16, 185, 129, 0.08);
    doc.setFontSize(60);
    doc.setFont('helvetica', 'bold');
    doc.text('PAID', 105, 160, { align: 'center', angle: 45 });
  } else if (inv.status === 'overdue') {
    doc.setTextColor(239, 68, 68, 0.06);
    doc.setFontSize(55);
    doc.text('OVERDUE', 105, 160, { align: 'center', angle: 45 });
  }
  doc.setTextColor(0, 0, 0);

  // ── Info block (3 columns) ───────────────────────────────────────────────
  // Col 1: FROM (Contractor)
  doc.setFillColor(...lightGray);
  doc.roundedRect(14, 44, 56, 38, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text('FROM', 19, 52);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  const cName = inv.contractor_name || 'Contractor';
  doc.text(doc.splitTextToSize(cName, 44)[0], 19, 59);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...gray);
  doc.text('Contractor · EXPO STAND', 19, 66);

  // Col 2: BILLED TO (Client)
  doc.setFillColor(...lightGray);
  doc.roundedRect(76, 44, 56, 38, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text('BILLED TO', 81, 52);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  const eName = inv.exhibitor_name || 'Client';
  doc.text(doc.splitTextToSize(eName, 44)[0], 81, 59);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...gray);
  doc.text('Exhibitor · EXPO STAND', 81, 66);

  // Col 3: INVOICE DETAILS
  doc.setFillColor(...lightGray);
  doc.roundedRect(138, 44, 57, 38, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text('INVOICE DETAILS', 143, 52);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(30, 41, 59);
  const detailsLeft = 143;
  const detailsRight = 192;
  doc.text('Project:', detailsLeft, 59);
  doc.text(doc.splitTextToSize(inv.project_title || '—', 35)[0], detailsRight, 59, { align: 'right' });
  doc.text('Issued:', detailsLeft, 65);
  doc.text(fmtDate(inv.issue_date), detailsRight, 65, { align: 'right' });
  doc.text('Due Date:', detailsLeft, 71);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(inv.status === 'overdue' ? 239 : 30, inv.status === 'overdue' ? 68 : 41, inv.status === 'overdue' ? 68 : 59);
  doc.text(fmtDate(inv.due_date), detailsRight, 71, { align: 'right' });
  doc.setTextColor(30, 41, 59);
  doc.setFont('helvetica', 'normal');
  doc.text('Currency:', detailsLeft, 77);
  doc.text(inv.currency, detailsRight, 77, { align: 'right' });

  // Status badge
  const sColors = { draft: [100, 116, 139], sent: [59, 130, 246], paid: [16, 185, 129], overdue: [239, 68, 68], cancelled: [156, 163, 175] };
  const sc = sColors[inv.status] || sColors.draft;
  doc.setFillColor(...sc);
  doc.roundedRect(14, 87, 36, 9, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text((inv.status || 'draft').toUpperCase(), 32, 93, { align: 'center' });

  // ── Line items table ────────────────────────────────────────────────────
  doc.setTextColor(0, 0, 0);
  autoTable(doc, {
    startY: 102,
    head: [['Description', 'Qty', 'Unit Price', 'Total']],
    body: (inv.items || []).map(i => [
      i.description || '',
      i.quantity ?? 1,
      `${inv.currency} ${Number(i.unit_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      `${inv.currency} ${Number(i.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
    ]),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: primary, textColor: 255, fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: lightGray },
    columnStyles: {
      0: { cellWidth: 95 },
      1: { halign: 'center', cellWidth: 20 },
      2: { halign: 'right', cellWidth: 40 },
      3: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
    },
  });

  // ── Totals block ────────────────────────────────────────────────────────
  const finalY = doc.lastAutoTable.finalY + 6;
  // Right-aligned totals box
  doc.setFillColor(...lightGray);
  doc.roundedRect(120, finalY, 75, inv.tax_rate > 0 ? 36 : 26, 2, 2, 'F');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text('Subtotal:', 128, finalY + 9);
  doc.setTextColor(30, 41, 59);
  doc.text(`${inv.currency} ${Number(inv.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 192, finalY + 9, { align: 'right' });
  let yOff = 15;
  if (inv.tax_rate > 0) {
    doc.setTextColor(...gray);
    doc.text(`Tax (${(inv.tax_rate * 100).toFixed(0)}%):`, 128, finalY + yOff);
    doc.setTextColor(30, 41, 59);
    doc.text(`${inv.currency} ${Number(inv.tax_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 192, finalY + yOff, { align: 'right' });
    yOff += 6;
  }
  // Total divider line
  doc.setDrawColor(...primary);
  doc.line(128, finalY + yOff + 2, 192, finalY + yOff + 2);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...primary);
  doc.text('TOTAL', 128, finalY + yOff + 10);
  doc.setFontSize(12);
  doc.text(`${inv.currency} ${Number(inv.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 192, finalY + yOff + 10, { align: 'right' });

  // Notes
  if (inv.notes) {
    const notesY = finalY + (inv.tax_rate > 0 ? 50 : 42);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text('NOTES', 14, notesY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(inv.notes, 14, notesY + 6, { maxWidth: 100 });
  }

  // ── Payment Information ─────────────────────────────────────────────────
  const pi = inv.payment_info;
  if (pi && (pi.bank_name || pi.iban)) {
    const piY = finalY + (inv.tax_rate > 0 ? 46 : 38) + (inv.notes ? 18 : 0);
    doc.setFillColor(240, 253, 244); // light green
    doc.roundedRect(14, piY, 100, pi.payment_notes ? 38 : 30, 2, 2, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...primary);
    doc.text('PAYMENT INFORMATION', 19, piY + 7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(30, 41, 59);
    let piRow = piY + 13;
    if (pi.account_holder) { doc.text(`Account: ${pi.account_holder}`, 19, piRow); piRow += 6; }
    if (pi.bank_name)       { doc.text(`Bank: ${pi.bank_name}`, 19, piRow); piRow += 6; }
    if (pi.iban)            { doc.setFont('helvetica', 'bold'); doc.text(`IBAN: ${pi.iban}`, 19, piRow); doc.setFont('helvetica', 'normal'); piRow += 6; }
    if (pi.swift_bic)       { doc.text(`SWIFT: ${pi.swift_bic}`, 19, piRow); piRow += 6; }
    if (pi.payment_notes) {
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(7.5);
      doc.text(pi.payment_notes, 19, piRow, { maxWidth: 90 });
    }
  }

  // ── Footer ──────────────────────────────────────────────────────────────
  const pageH = doc.internal.pageSize.height;
  doc.setFillColor(...primary);
  doc.rect(0, pageH - 14, 210, 14, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Generated by EXPO STAND Contractor Portal  ·  www.expostand.com', 14, pageH - 5);
  doc.text(`Page 1 of 1`, 196, pageH - 5, { align: 'right' });

  doc.save(`${inv.invoice_number}.pdf`);
};

export default function ContractorInvoices() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [contractorPaymentInfo, setContractorPaymentInfo] = useState(null);
  const [contractorLogoUrl, setContractorLogoUrl] = useState(null);

  // Create form state
  const [form, setForm] = useState({
    exhibitor_org_id: '',
    project_title: '',
    items: [{ ...EMPTY_ITEM }],
    tax_rate: 0,
    currency: 'EUR',
    due_date: '',
    notes: '',
  });
  const [creating, setCreating] = useState(false);
  const [exhibitors, setExhibitors] = useState([]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/contractor/invoices?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load invoices'); }
    finally { setLoading(false); }
  }, [token, page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Load exhibitor orgs for dropdown + contractor payment info
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/contractor/client-orgs`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setExhibitors(res.data.orgs || []))
      .catch(() => setExhibitors([]));
    // Load contractor profile for payment_info and logo_url (for PDF)
    axios.get(`${API}/contractor/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const prof = res.data?.profile || {};
        setContractorPaymentInfo(prof.payment_info || null);
        setContractorLogoUrl(prof.logo_url || null);
      })
      .catch(() => {});
  }, [token]);

  const updateItem = (idx, field, val) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: field === 'description' ? val : parseFloat(val) || 0 };
      return { ...f, items };
    });
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, i) => s + (i.unit_price * i.quantity), 0);
  const taxAmount = subtotal * form.tax_rate;
  const totalAmount = subtotal + taxAmount;

  const handleCreate = async () => {
    if (!form.project_title.trim()) { toast.error('Project title is required'); return; }
    if (form.items.some(i => !i.description.trim())) { toast.error('All items need a description'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/contractor/invoices`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice created');
      setShowCreate(false);
      setForm({ exhibitor_org_id: '', project_title: '', items: [{ ...EMPTY_ITEM }], tax_rate: 0, currency: 'EUR', due_date: '', notes: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create invoice'); }
    finally { setCreating(false); }
  };

  const handleStatusChange = async (invId, newStatus) => {
    try {
      await axios.patch(`${API}/invoices/${invId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Status updated to ${newStatus}`);
      setInvoices(prev => prev.map(i => i.id === invId ? { ...i, status: newStatus } : i));
      if (selectedInvoice?.id === invId) setSelectedInvoice(p => ({ ...p, status: newStatus }));
    } catch { toast.error('Failed to update status'); }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}?`)) return;
    try {
      await axios.delete(`${API}/invoices/${inv.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice deleted');
      setInvoices(prev => prev.filter(i => i.id !== inv.id));
      if (selectedInvoice?.id === inv.id) setSelectedInvoice(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete invoice'); }
  };

  const filtered = invoices.filter(i =>
    search === '' || i.invoice_number?.toLowerCase().includes(search.toLowerCase()) ||
    i.project_title?.toLowerCase().includes(search.toLowerCase()) ||
    i.exhibitor_name?.toLowerCase().includes(search.toLowerCase())
  );

  // KPI stats
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total_amount || 0), 0);
  const totalPending = invoices.filter(i => i.status === 'sent').reduce((s, i) => s + (i.total_amount || 0), 0);
  const totalOverdue = invoices.filter(i => i.status === 'overdue').reduce((s, i) => s + (i.total_amount || 0), 0);
  const countDraft = invoices.filter(i => i.status === 'draft').length;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-600 to-emerald-500 flex items-center justify-center shadow-md">
              <Receipt className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Invoices</h1>
              <p className="text-xs text-gray-500">{total} total invoices</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2">
            <Plus className="w-4 h-4" /> New Invoice
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6 space-y-5">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Received', value: fmt(totalPaid, 'EUR'), icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
            { label: 'Awaiting Payment', value: fmt(totalPending, 'EUR'), icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
            { label: 'Overdue', value: fmt(totalOverdue, 'EUR'), icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
            { label: 'Draft', value: countDraft, icon: FileText, color: 'text-slate-600', bg: 'bg-slate-50' },
          ].map(kpi => {
            const Icon = kpi.icon;
            return (
              <div key={kpi.label} className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-4 shadow-sm">
                <div className={`w-11 h-11 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${kpi.color}`} />
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                  <p className="text-xl font-black text-gray-900">{kpi.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search invoices..." className="pl-9 h-9" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {['all', 'draft', 'sent', 'paid', 'overdue'].map(s => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold capitalize transition-all ${statusFilter === s ? 'bg-green-600 text-white shadow' : 'bg-white border border-gray-200 text-gray-600 hover:border-green-400'}`}>
                {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Invoice #', 'Client', 'Project', 'Amount', 'Due Date', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="py-16 text-center">
                  <Loader2 className="w-6 h-6 animate-spin text-green-600 mx-auto" />
                </td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="py-16 text-center">
                  <Receipt className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 font-medium">No invoices yet</p>
                  <button onClick={() => setShowCreate(true)} className="mt-3 text-sm text-green-600 hover:underline font-semibold">Create your first invoice</button>
                </td></tr>
              ) : filtered.map(inv => (
                <tr key={inv.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setSelectedInvoice(inv)}>
                  <td className="px-4 py-3 font-mono text-xs font-bold text-gray-800">{inv.invoice_number}</td>
                  <td className="px-4 py-3 text-gray-700">{inv.exhibitor_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{inv.project_title || '—'}</td>
                  <td className="px-4 py-3 font-bold text-gray-900">{fmt(inv.total_amount, inv.currency)}</td>
                  <td className="px-4 py-3 text-gray-500">{fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSelectedInvoice(inv)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-blue-50" title="View">
                        <Eye className="w-4 h-4" />
                      </button>
                      <button onClick={() => downloadPDF({...inv, payment_info: contractorPaymentInfo, logo_url: contractorLogoUrl})} className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50" title="Download PDF">
                        <Download className="w-4 h-4" />
                      </button>
                      {inv.status === 'draft' && (
                        <>
                          <button onClick={() => handleStatusChange(inv.id, 'sent')} className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg hover:bg-green-50" title="Send">
                            <Send className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(inv)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {inv.status === 'sent' && (
                        <button onClick={() => handleStatusChange(inv.id, 'paid')} className="p-1.5 text-gray-400 hover:text-emerald-600 rounded-lg hover:bg-emerald-50" title="Mark Paid">
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Detail Modal */}
      {selectedInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedInvoice(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              {/* Invoice Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <Receipt className="w-6 h-6 text-green-600" />
                    <h2 className="text-xl font-black text-gray-900">{selectedInvoice.invoice_number}</h2>
                    <StatusBadge status={selectedInvoice.status} />
                  </div>
                  <p className="text-gray-500 text-sm">{selectedInvoice.project_title}</p>
                </div>
                <button onClick={() => setSelectedInvoice(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Billed To</p>
                  <p className="font-semibold text-gray-800">{selectedInvoice.exhibitor_name || 'Client'}</p>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4">
                  <p className="text-xs font-bold text-gray-400 uppercase mb-1">Due Date</p>
                  <p className="font-semibold text-gray-800">{fmtDate(selectedInvoice.due_date)}</p>
                </div>
              </div>

              {/* Line Items */}
              <table className="w-full text-sm mb-4">
                <thead className="border-b-2 border-gray-200">
                  <tr>
                    <th className="text-left py-2 text-xs font-bold text-gray-500 uppercase">Description</th>
                    <th className="text-right py-2 text-xs font-bold text-gray-500 uppercase">Qty</th>
                    <th className="text-right py-2 text-xs font-bold text-gray-500 uppercase">Unit Price</th>
                    <th className="text-right py-2 text-xs font-bold text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(selectedInvoice.items || []).map((item, i) => (
                    <tr key={i}>
                      <td className="py-2.5 text-gray-800">{item.description}</td>
                      <td className="py-2.5 text-right text-gray-600">{item.quantity}</td>
                      <td className="py-2.5 text-right text-gray-600">{fmt(item.unit_price, selectedInvoice.currency)}</td>
                      <td className="py-2.5 text-right font-semibold text-gray-900">{fmt(item.total, selectedInvoice.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="ml-auto max-w-xs space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span><span>{fmt(selectedInvoice.subtotal, selectedInvoice.currency)}</span>
                </div>
                {selectedInvoice.tax_rate > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>Tax ({(selectedInvoice.tax_rate * 100).toFixed(0)}%)</span>
                    <span>{fmt(selectedInvoice.tax_amount, selectedInvoice.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between font-black text-lg text-gray-900 border-t border-gray-200 pt-2">
                  <span>Total</span><span>{fmt(selectedInvoice.total_amount, selectedInvoice.currency)}</span>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                  <strong>Notes:</strong> {selectedInvoice.notes}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                {selectedInvoice.status === 'draft' && (
                  <>
                    <Button onClick={() => handleStatusChange(selectedInvoice.id, 'sent')} className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2">
                      <Send className="w-4 h-4" />Send Invoice
                    </Button>
                    <Button variant="destructive" onClick={() => handleDelete(selectedInvoice)} className="flex items-center gap-2">
                      <Trash2 className="w-4 h-4" />Delete
                    </Button>
                  </>
                )}
                {selectedInvoice.status === 'sent' && (
                  <Button onClick={() => handleStatusChange(selectedInvoice.id, 'paid')} className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />Mark as Paid
                  </Button>
                )}
                <Button variant="outline" onClick={() => downloadPDF({...selectedInvoice, payment_info: contractorPaymentInfo, logo_url: contractorLogoUrl})} className="ml-auto flex items-center gap-2">
                  <Download className="w-4 h-4" />Download PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-xl font-black text-gray-900 flex items-center gap-2"><Plus className="w-5 h-5 text-green-600" />New Invoice</h2>
                <button onClick={() => setShowCreate(false)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5" /></button>
              </div>

              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Project / Event Title *</label>
                    <Input value={form.project_title} onChange={e => setForm(f => ({...f, project_title: e.target.value}))} placeholder="e.g. Hannover Messe 2025 — Stand Design" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Client (Exhibitor Org)</label>
                    <select value={form.exhibitor_org_id} onChange={e => setForm(f => ({...f, exhibitor_org_id: e.target.value}))} className="w-full rounded-md border border-input px-3 py-2 text-sm">
                      <option value="">— Select (optional) —</option>
                      {exhibitors.map(o => <option key={o.id} value={o.id}>{o.display_name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Currency</label>
                    <select value={form.currency} onChange={e => setForm(f => ({...f, currency: e.target.value}))} className="w-full rounded-md border border-input px-3 py-2 text-sm">
                      {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Due Date</label>
                    <Input type="date" value={form.due_date} onChange={e => setForm(f => ({...f, due_date: e.target.value}))} />
                  </div>
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-bold text-gray-500 uppercase">Line Items *</label>
                    <button onClick={addItem} className="text-xs text-green-600 hover:text-green-700 font-semibold flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Add Item
                    </button>
                  </div>
                  <div className="space-y-2">
                    {form.items.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                        <input value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                          placeholder="Description" className="col-span-5 rounded-md border border-input px-3 py-2 text-sm" />
                        <input type="number" min={1} value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                          placeholder="Qty" className="col-span-2 rounded-md border border-input px-3 py-2 text-sm text-center" />
                        <input type="number" min={0} step={100} value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)}
                          placeholder="Price" className="col-span-3 rounded-md border border-input px-3 py-2 text-sm text-right" />
                        <div className="col-span-1 text-right text-xs font-bold text-gray-600">
                          {(item.quantity * item.unit_price).toLocaleString()}
                        </div>
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="col-span-1 text-gray-300 hover:text-red-400">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Tax */}
                <div className="flex items-center gap-4 flex-wrap">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Tax Rate</label>
                    <select value={form.tax_rate} onChange={e => setForm(f => ({...f, tax_rate: parseFloat(e.target.value)}))} className="rounded-md border border-input px-3 py-2 text-sm">
                      {TAX_RATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-sm text-gray-500">Subtotal: <span className="font-semibold">{fmt(subtotal, form.currency)}</span></div>
                    {form.tax_rate > 0 && <div className="text-sm text-gray-500">Tax: <span className="font-semibold">{fmt(taxAmount, form.currency)}</span></div>}
                    <div className="text-xl font-black text-gray-900">Total: {fmt(totalAmount, form.currency)}</div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Notes (Optional)</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                    rows={2} placeholder="Payment terms, bank details, special notes..."
                    className="w-full rounded-md border border-input px-3 py-2 text-sm resize-none" />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button onClick={handleCreate} disabled={creating} className="flex-1 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                    Save as Draft
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreate(false)} className="flex-1">Cancel</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
